import React from 'react';
import { connect } from 'react-redux';
import { Slider, Grid, CircularProgress, Typography } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import LyricsSegment from './LyricsSegment';
import { secondsToDigitalText } from '../../../../../utilities/timeUtilities';
import CreatePageConstants from '../../CreatePageConstants';
import TimelineCursor from './TimelineCursor';
import { createActions } from '../../store';
import TimelineActionsButton from './TimelineActionsButton';
import { detectLyricsSegmentAddPress } from '../../../../../utilities/eventUtilities';
import { randomString } from '../../../../../utilities/randomUtilities';
import { getOSCommandKeyName } from '../../../../../utilities/stringUtilities';
import { convertToLyricsTime } from '../../../../../utilities/lyricsUtilities';

const styles = theme => ({
    mainContainer: {
        position: 'relative'
    },
    timelineLoader: {
        position: 'relative',
        right: 45
    },
    hotkeysLabel: {
        position: 'relative',
        right: 20,
        top: 50
    }
});

const VerticalTimelineSlider = withStyles(Slider, {
    root: {
        position: 'relative',
        left: 30,
        cursor: 'default',
    },
    mark: {
        display: 'none'
    },
    markLabel: {
        fontSize: '8pt',
        left: '-33px !important',
        textAlign: 'right',
        display: 'inline-block',
        width: 40
    },
    rail: {
        background: 'white'
    },
    thumb: {
        display: 'none'
    }
});

class SegmentsTimeline extends React.Component {
    state = {
        sliderMarks: []
    };
    actionsPanelDomElement = undefined;

    render() {
        const { classes, audioDuration, lyricsSegments, settingsPanelScrollData } = this.props;
        const { sliderMarks } = this.state;
        
        const scaleFactor = CreatePageConstants.LYRICS_TIMELINE.SCALE_FACTOR;
        const audioReadyToUse = audioDuration !== undefined && audioDuration > 0;
        
        return (
            <Grid 
                id='lyrics-timeline-container'
                container
                direction="row"
                justifyContent="flex-start"
                className={classes.mainContainer}
            >
                <Grid
                    size={{ xs: 3 }} 
                    style={{height: audioDuration * scaleFactor}}
                >
                    {audioReadyToUse &&
                        <>
                            {/* Render the cursor of current audion position*/}
                            <TimelineCursor />

                            <VerticalTimelineSlider
                                size="small"
                                min={0}
                                step={0.1}
                                max={audioDuration}
                                orientation='vertical'
                                value={null}
                                marks={sliderMarks}
                                onClick={() => { return false }}
                            />
                        </>
                    }
                </Grid>
                <Grid
                    id='lyrics-segments-container' 
                    style={{height: audioDuration * scaleFactor}}
                    size={{ xs: 9 }}
                    onMouseLeave={this.onTimelineMouseLeave}
                    onMouseMove={this.onTimelineMouseMove}
                >
                    {audioReadyToUse
                    ?
                        this.renderSegmentsRange(lyricsSegments, settingsPanelScrollData)
                    :
                        <CircularProgress className={classes.timelineLoader}/>
                    }
                    
                    {(audioReadyToUse && (lyricsSegments === undefined || lyricsSegments.length === 0)) &&
                        <Typography 
                            className={classes.hotkeysLabel}
                            color='textSecondary'
                            variant='caption'
                        >
                            Add lyrics by clicking here or pressing <br/> '{getOSCommandKeyName()}' + 'Enter'
                        </Typography>
                    }

                    {/* Render the button with timeline context menu */}
                    <TimelineActionsButton />
                </Grid>
            </Grid>
        );
    }

    getSegmentsRangeCoefficientForViewportHeight = (viewportHeight) => {
        if (viewportHeight < 200)
            return 6;
        if (viewportHeight < 400)
            return 3;
        if (viewportHeight < 800)
            return 2;
        if (viewportHeight < 1200)
            return 1;

        return 1;
    }

    renderSegmentsRange = (lyricsSegments, settingsPanelScrollData) => {
        const viewportStartTime = convertToLyricsTime(settingsPanelScrollData.viewportStartPosition / CreatePageConstants.LYRICS_TIMELINE.SCALE_FACTOR);
        const viewportDuration = convertToLyricsTime(settingsPanelScrollData.viewportHeight / CreatePageConstants.LYRICS_TIMELINE.SCALE_FACTOR);
        const viewportEndTime = convertToLyricsTime(viewportStartTime + viewportDuration);

        // The start time value can be negative at the beginning, because of top controls and buttons in a panel.
        // But it doesn't affect the rendering range so match.

        const segmentsRangeCoefficient = this.getSegmentsRangeCoefficientForViewportHeight(settingsPanelScrollData.viewportHeight);
        let rangeStartTime = convertToLyricsTime(viewportStartTime - (viewportDuration * segmentsRangeCoefficient));
        let rangeEndTime = convertToLyricsTime(viewportEndTime + (viewportDuration * segmentsRangeCoefficient));

        return (
            lyricsSegments.map((lyricsSegment, index) => (
                (lyricsSegment.startTime <= rangeEndTime && lyricsSegment.endTime >= rangeStartTime) 
                && <LyricsSegment 
                    key={lyricsSegment.id}
                    index={index}
                />
            ))
        )
    }

    componentDidMount() {
        const { audioDuration } = this.props;

        if (audioDuration !== 0)
            this.createTimelineMarks(audioDuration);

        this.actionsPanelDomElement = document.getElementById('timeline-actions-panel');

        // Add event listener to handle the hot keys
        document.addEventListener('keyup', this.onDocumentKeyUp, false);
    }

    componentWillUnmount() {
        // Remove event listener to handle the hot keys
        document.removeEventListener('keyup', this.onDocumentKeyUp);
    }

    componentDidUpdate(prevProps) {
        // Update the timeline marks when track duration has changed
        if (this.props.audioDuration !== prevProps.audioDuration) {
            this.createTimelineMarks(this.props.audioDuration);
        }
    }

    onDocumentKeyUp = (event) => {
        let addAfterCurrentSegment = (document.activeElement.tagName === 'TEXTAREA');

        detectLyricsSegmentAddPress(event, () => {
            const duration = CreatePageConstants.LYRICS_TIMELINE.DEFAULT_SEGMENT_DURATION;

            // The segment without time will be added to the playback time
            const newSegment = {
                id: randomString(),
                text: CreatePageConstants.LYRICS_TIMELINE.DEFAULT_SEGMENT_TEXT,
                startTime: null,
                endTime: null
            }

            // If the segment text is focused we add a new one after current segment
            if (addAfterCurrentSegment) {
                const segmentContainer = document.activeElement.closest('.MuiPaper-root');
                newSegment.startTime = parseFloat(segmentContainer.getAttribute('endtime'));
                newSegment.endTime = newSegment.startTime + duration;
            }
            
            this.props.addLyricsSegmentToFreePlace(newSegment, duration, !addAfterCurrentSegment);
        }); 
    }

    onTimelineMouseMove = (event) => {
        if (!this.props.lyricsSegmentDragging && !this.props.lyricsTimelineContextMenuOpen && !this.props.previewPositionDragging) {
            const containerRect = event.currentTarget.getBoundingClientRect()
            const cursorPosition = event.pageY - containerRect.top;
            const panelHeight = CreatePageConstants.LYRICS_TIMELINE.DEFAULT_SEGMENT_DURATION * CreatePageConstants.LYRICS_TIMELINE.SCALE_FACTOR;

            const startPosition = (cursorPosition - (panelHeight / 2));
            const endPosition = (cursorPosition + (panelHeight / 2));

            const startTime = convertToLyricsTime(startPosition / CreatePageConstants.LYRICS_TIMELINE.SCALE_FACTOR);
            const endTime = convertToLyricsTime(endPosition / CreatePageConstants.LYRICS_TIMELINE.SCALE_FACTOR);
            
            this.props.setLyricsActionsPosition(startTime, endTime);
        }
    }
    
    onTimelineMouseLeave = () => {
        const { hideLyricsActionsButton, setProperty } = this.props;
        setProperty('lyricsTimelineContextMenuOpen', false);
        hideLyricsActionsButton();
    }

    createTimelineMarks = (duration) => {
        const marks = [{value: duration, label: `${secondsToDigitalText(0)} ---`}];

        for (let seconds = 0; seconds < duration; seconds++) {
            marks.push({value: seconds, label: `${secondsToDigitalText(duration - seconds)} ---`});
        }

        this.setState({sliderMarks: marks});
    }
}

const mapStateToProps = (state) => {
    const { 
        audioDuration,
        lyricsTimelineContextMenuOpen,
        lyricsSegmentDragging,
        previewPositionDragging,
        settingsPanelScrollData
    } = state.create; 

    const { segments: lyricsSegments } = state.createPreview.present.videoSettings.lyrics;

    return {
        audioDuration,
        lyricsSegments,
        lyricsTimelineContextMenuOpen,
        lyricsSegmentDragging,
        previewPositionDragging,
        settingsPanelScrollData
    };
}

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const setLyricsActionsPosition = (startTime, endTime) => dispatch(createActions.setLyricsActionsPosition(startTime, endTime));
    const addLyricsSegmentToFreePlace = (newSegment, duration, addToPlaybackPosition) => dispatch(createActions.addLyricsSegmentToFreePlace(newSegment, duration, addToPlaybackPosition));
    const hideLyricsActionsButton = () => dispatch(createActions.setProperty('lyricsActionsPanelTime', -1));

    return {
        setProperty,
        setLyricsActionsPosition,
        addLyricsSegmentToFreePlace,
        hideLyricsActionsButton
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(SegmentsTimeline, styles));